import { SeededRandom } from './random.js';
const DIRECTIONS = [
    { x: 1, z: 0 },
    { x: -1, z: 0 },
    { x: 0, z: 1 },
    { x: 0, z: -1 },
];
function coordinateKey(x, z) {
    return `${x},${z}`;
}
function edgeKey(a, b) {
    return a < b ? `${a}|${b}` : `${b}|${a}`;
}
function roomId(index) {
    return `room-${String(index).padStart(3, '0')}`;
}
function connectionId(index) {
    return `connection-${String(index).padStart(3, '0')}`;
}
function buildAdjacency(cells, connections) {
    const out = new Map(cells.map((cell) => [cell.id, new Set()]));
    for (const edge of connections) {
        out.get(edge.a)?.add(edge.b);
        out.get(edge.b)?.add(edge.a);
    }
    return out;
}
function breadthFirst(startId, cells, connections) {
    const adjacency = buildAdjacency(cells, connections);
    const distances = new Map([[startId, 0]]);
    const parents = new Map();
    const queue = [startId];
    while (queue.length > 0) {
        const current = queue.shift();
        const nextDistance = (distances.get(current) ?? 0) + 1;
        const neighbours = [...(adjacency.get(current) ?? [])].sort();
        for (const neighbour of neighbours) {
            if (distances.has(neighbour))
                continue;
            distances.set(neighbour, nextDistance);
            parents.set(neighbour, current);
            queue.push(neighbour);
        }
    }
    return { distances, parents };
}
function pathTo(startId, targetId, parents) {
    const reversed = [targetId];
    let cursor = targetId;
    while (cursor !== startId) {
        const parent = parents.get(cursor);
        if (!parent)
            throw new Error(`no path from '${startId}' to '${targetId}'`);
        reversed.push(parent);
        cursor = parent;
    }
    return reversed.reverse();
}
function hasFreeNeighbour(cell, occupied) {
    return DIRECTIONS.some((direction) => !occupied.has(coordinateKey(cell.x + direction.x, cell.z + direction.z)));
}
function pickTemplate(theme, role, random) {
    const candidates = theme.roomTemplates.filter((template) => template.roles.includes(role));
    return random.weightedPick(candidates, (template) => template.selectionWeight).id;
}
function pickOrdinaryRole(criticalPath, pathIndex, profile, optionalBonus, random) {
    if (criticalPath &&
        pathIndex > 1 &&
        profile.restEveryRooms > 0 &&
        pathIndex % profile.restEveryRooms === 0) {
        return 'rest';
    }
    const roll = random.next();
    if (criticalPath) {
        if (roll < profile.combatRoomChance)
            return 'combat';
        if (roll < profile.combatRoomChance + profile.puzzleRoomChance)
            return 'puzzle';
        return 'traversal';
    }
    const treasureChance = Math.min(0.65, profile.treasureRoomChance + optionalBonus);
    const combatChance = Math.min(0.75, profile.combatRoomChance * 0.72);
    const puzzleChance = Math.min(0.3, profile.puzzleRoomChance + optionalBonus * 0.25);
    if (roll < treasureChance)
        return 'treasure';
    if (roll < treasureChance + combatChance)
        return 'combat';
    if (roll < treasureChance + combatChance + puzzleChance)
        return 'puzzle';
    return 'traversal';
}
export function generateTopology(input) {
    const { random } = input;
    const baseRoomCount = input.roomCount - 1;
    const cells = [{ id: roomId(0), x: 0, z: 0, treeDepth: 0 }];
    const occupied = new Map([[coordinateKey(0, 0), cells[0]]]);
    const connections = [];
    while (cells.length < baseRoomCount) {
        const frontier = [];
        for (const cell of cells) {
            for (const direction of DIRECTIONS) {
                const x = cell.x + direction.x;
                const z = cell.z + direction.z;
                if (!occupied.has(coordinateKey(x, z)))
                    frontier.push({ from: cell, x, z });
            }
        }
        const chosen = random.weightedPick(frontier, (entry) => 1 + entry.from.treeDepth * 1.35);
        const cell = {
            id: roomId(cells.length),
            x: chosen.x,
            z: chosen.z,
            treeDepth: chosen.from.treeDepth + 1,
        };
        cells.push(cell);
        occupied.set(coordinateKey(cell.x, cell.z), cell);
        connections.push({
            id: connectionId(connections.length),
            a: chosen.from.id,
            b: cell.id,
            kind: random.next() < 0.42 ? 'door' : 'open',
        });
    }
    const existingEdges = new Set(connections.map((edge) => edgeKey(edge.a, edge.b)));
    const loopCandidates = [];
    for (const cell of cells) {
        for (const direction of DIRECTIONS) {
            const neighbour = occupied.get(coordinateKey(cell.x + direction.x, cell.z + direction.z));
            if (!neighbour || neighbour.id <= cell.id)
                continue;
            const key = edgeKey(cell.id, neighbour.id);
            if (!existingEdges.has(key))
                loopCandidates.push({ a: cell, b: neighbour });
        }
    }
    const loopBonus = input.mutators.reduce((sum, mutator) => sum + (mutator.effects.loopChanceBonus ?? 0), 0);
    const loopChance = Math.min(0.75, input.profile.loopChance + loopBonus);
    const maxLoops = Math.max(1, Math.floor(baseRoomCount / 3));
    let addedLoops = 0;
    for (const candidate of random.shuffle(loopCandidates)) {
        if (addedLoops >= maxLoops || random.next() >= loopChance)
            continue;
        connections.push({
            id: connectionId(connections.length),
            a: candidate.a.id,
            b: candidate.b.id,
            kind: random.next() < 0.5 ? 'door' : 'open',
        });
        existingEdges.add(edgeKey(candidate.a.id, candidate.b.id));
        addedLoops += 1;
    }
    const entryRoomId = cells[0]?.id ?? roomId(0);
    const search = breadthFirst(entryRoomId, cells, connections);
    const bossCandidates = cells.filter((cell) => cell.id !== entryRoomId && hasFreeNeighbour(cell, occupied));
    const maximumDistance = Math.max(...bossCandidates.map((cell) => search.distances.get(cell.id) ?? 0));
    const farthest = bossCandidates.filter((cell) => (search.distances.get(cell.id) ?? 0) === maximumDistance);
    const finalBossCell = random.pick(farthest);
    const pathToBoss = pathTo(entryRoomId, finalBossCell.id, search.parents);
    const freeExitDirections = random.shuffle(DIRECTIONS.filter((direction) => !occupied.has(coordinateKey(finalBossCell.x + direction.x, finalBossCell.z + direction.z))));
    const exitDirection = freeExitDirections[0];
    if (!exitDirection)
        throw new Error('final boss room has no free coordinate for the reward exit');
    const exitCell = {
        id: roomId(cells.length),
        x: finalBossCell.x + exitDirection.x,
        z: finalBossCell.z + exitDirection.z,
        treeDepth: (search.distances.get(finalBossCell.id) ?? finalBossCell.treeDepth) + 1,
    };
    cells.push(exitCell);
    occupied.set(coordinateKey(exitCell.x, exitCell.z), exitCell);
    connections.push({
        id: connectionId(connections.length),
        a: finalBossCell.id,
        b: exitCell.id,
        kind: 'sealed-gate',
    });
    const criticalPathRoomIds = [...pathToBoss, exitCell.id];
    const criticalSet = new Set(criticalPathRoomIds);
    const bossRoomIds = [];
    let midBossRoomId;
    if (input.bossCount === 2) {
        const minimumIndex = Math.min(2, Math.max(1, pathToBoss.length - 2));
        const maximumIndex = Math.max(minimumIndex, pathToBoss.length - 2);
        const target = Math.round((pathToBoss.length - 1) * (0.55 + random.next() * 0.15));
        const index = Math.max(minimumIndex, Math.min(maximumIndex, target));
        midBossRoomId = pathToBoss[index];
        if (!midBossRoomId || midBossRoomId === finalBossCell.id || midBossRoomId === entryRoomId) {
            throw new Error('two-boss topology requires a distinct mid-path boss room');
        }
        bossRoomIds.push(midBossRoomId);
    }
    bossRoomIds.push(finalBossCell.id);
    const optionalBonus = input.mutators.reduce((sum, mutator) => sum + (mutator.effects.optionalRoomChanceBonus ?? 0), 0);
    const pathIndexById = new Map(criticalPathRoomIds.map((id, index) => [id, index]));
    const roles = new Map();
    roles.set(entryRoomId, 'entrance');
    roles.set(finalBossCell.id, 'boss');
    roles.set(exitCell.id, 'exit');
    if (midBossRoomId)
        roles.set(midBossRoomId, 'miniboss');
    for (const cell of cells) {
        if (roles.has(cell.id))
            continue;
        const pathIndex = pathIndexById.get(cell.id) ?? -1;
        roles.set(cell.id, pickOrdinaryRole(criticalSet.has(cell.id), pathIndex, input.profile, optionalBonus, random));
    }
    if (![...roles.values()].includes('combat')) {
        const fallback = pathToBoss.find((id) => id !== entryRoomId && id !== finalBossCell.id && id !== midBossRoomId);
        if (fallback)
            roles.set(fallback, 'combat');
    }
    const rooms = cells.map((cell) => {
        const role = roles.get(cell.id) ?? 'traversal';
        return {
            id: cell.id,
            coordinate: { x: cell.x, z: cell.z },
            depth: cell.id === exitCell.id
                ? (search.distances.get(finalBossCell.id) ?? 0) + 1
                : (search.distances.get(cell.id) ?? cell.treeDepth),
            role,
            criticalPath: criticalSet.has(cell.id),
            templateId: pickTemplate(input.theme, role, random),
            rotationQuarterTurns: random.int(0, 3),
            objectiveIds: [],
        };
    });
    return {
        rooms,
        connections,
        entryRoomId,
        exitRoomId: exitCell.id,
        finalBossRoomId: finalBossCell.id,
        bossRoomIds,
        criticalPathRoomIds,
    };
}
//# sourceMappingURL=topology.js.map